import { ShieldCheck } from 'lucide-react-native';
import { useCallback, useMemo, useState } from 'react';
import { Button } from '../../../components/primitives/Button';
import { FormInput } from '../../../components/primitives/FormInput';
import { Select } from '../../../components/primitives/Select';
import { Sheet } from '../../../components/primitives/Sheet';
import { HStack, VStack } from '../../../components/primitives/Stack';
import { Text } from '../../../components/primitives/Text';
import { useModal } from '../../../contexts/ModalContext';
import { useToast } from '../../../contexts/ToastContext';
import { useTranslation } from '../../../contexts/TranslationContext';
import { useLeadConsent, useRecordConsent } from '../../../hooks/useConsent';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import type { ConsentSource } from '../../../types/consent';
import { consentSourceLabelKey } from '../utils/consentSourceLabel';
import { ConsentBadge } from './ConsentBadge';

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

interface ConsentSectionProps {
  leadId: string;
  canManage: boolean;
}

export function ConsentSection({ leadId, canManage }: Readonly<ConsentSectionProps>) {
  const { t } = useTranslation();
  const { confirm } = useModal();
  const { showSuccess, showError } = useToast();
  const consentQuery = useLeadConsent(leadId);
  const recordConsent = useRecordConsent();

  const [grantOpen, setGrantOpen] = useState(false);
  const [source, setSource] = useState<ConsentSource>('manual');
  const [evidenceUrl, setEvidenceUrl] = useState('');
  const [occurredAt, setOccurredAt] = useState(todayIso());
  const [dateError, setDateError] = useState<string | null>(null);

  const status = consentQuery.data?.dataProcessing ?? null;

  const sourceOptions = useMemo(
    () => [
      { value: 'manual', label: t('leads.detail.consent.sourceManual') },
      { value: 'form', label: t('leads.detail.consent.sourceForm') },
      { value: 'whatsapp', label: t('leads.detail.consent.sourceWhatsapp') },
      { value: 'web', label: t('leads.detail.consent.sourceWeb') },
    ],
    [t],
  );

  const historyCaption = useMemo(() => {
    if (!status) return null;
    const date = status.occurredAt ? status.occurredAt.slice(0, 10) : '';
    const sourceLabel = t(consentSourceLabelKey(status.source));
    return t(status.granted ? 'leads.detail.consent.grantedOn' : 'leads.detail.consent.refusedOn', {
      date,
      source: sourceLabel,
    });
  }, [status, t]);

  const handleError = useCallback(
    (error: unknown) => {
      const status = (error as { response?: { status?: number } } | null)?.response?.status;
      if (status === 403) {
        showError(t('leads.detail.consent.forbidden'));
        return;
      }
      showError(t('leads.detail.consent.error'));
    },
    [showError, t],
  );

  const openGrant = useCallback(() => {
    setSource('manual');
    setEvidenceUrl('');
    setOccurredAt(todayIso());
    setDateError(null);
    setGrantOpen(true);
  }, []);

  const submitGrant = useCallback(() => {
    if (!ISO_DATE_RE.test(occurredAt)) {
      setDateError(t('leads.detail.consent.dateInvalid'));
      return;
    }
    setDateError(null);
    const trimmedUrl = evidenceUrl.trim();
    recordConsent.mutate(
      {
        leadId,
        consentType: 'data_processing',
        granted: true,
        source,
        ...(trimmedUrl ? { evidenceUrl: trimmedUrl } : {}),
        occurredAt: new Date(`${occurredAt}T00:00:00.000Z`).toISOString(),
      },
      {
        onSuccess: () => {
          setGrantOpen(false);
          showSuccess(t('leads.detail.consent.successGranted'));
        },
        onError: handleError,
      },
    );
  }, [occurredAt, evidenceUrl, recordConsent, leadId, source, showSuccess, handleError, t]);

  const handleRevoke = useCallback(async () => {
    const confirmed = await confirm({
      title: t('leads.detail.consent.revokeTitle'),
      message: t('leads.detail.consent.revokeBody'),
      confirmLabel: t('leads.detail.consent.revoke'),
      variant: 'destructive',
    });
    if (!confirmed) return;
    recordConsent.mutate(
      {
        leadId,
        consentType: 'data_processing',
        granted: false,
        source: 'manual',
      },
      {
        onSuccess: () => showSuccess(t('leads.detail.consent.successRevoked')),
        onError: handleError,
      },
    );
  }, [confirm, recordConsent, leadId, showSuccess, handleError, t]);

  return (
    <HStack spacing="md" align="center" testID="lead_detail_consent_row">
      <ShieldCheck size={16} color={colors.text.tertiary} strokeWidth={1.5} />
      <VStack spacing="xs" style={{ flex: 1 }}>
        <Text variant="caption" color={colors.text.tertiary} style={{ fontSize: 10 }}>
          {t('leads.detail.consent.title')}
        </Text>
        <HStack spacing="sm" align="center" style={{ flexWrap: 'wrap' }}>
          <ConsentBadge status={status} />
          {canManage ? (
            status?.granted ? (
              <Button
                variant="outline"
                size="xs"
                pill
                onPress={handleRevoke}
                textColor={colors.status.error.main}
                style={{ borderColor: colors.status.error.main }}
                accessibilityLabel={t('leads.detail.consent.revoke')}
                accessibilityState={{ busy: recordConsent.isPending }}
                disabled={recordConsent.isPending}
                testID="lead_detail_consent_revoke"
              >
                {t('leads.detail.consent.revoke')}
              </Button>
            ) : (
              <Button
                variant="outline"
                size="xs"
                pill
                onPress={openGrant}
                accessibilityLabel={t('leads.detail.consent.grant')}
                disabled={recordConsent.isPending}
                testID="lead_detail_consent_grant"
              >
                {t('leads.detail.consent.grant')}
              </Button>
            )
          ) : null}
        </HStack>
        {historyCaption ? (
          <Text variant="caption" color={colors.text.tertiary} style={{ fontSize: 10 }}>
            {historyCaption}
          </Text>
        ) : null}
      </VStack>

      <Sheet
        visible={grantOpen}
        onClose={() => setGrantOpen(false)}
        title={t('leads.detail.consent.grantTitle')}
        position="center"
        size="sm"
      >
        <VStack spacing="lg">
          <Text variant="body" color={colors.text.secondary}>
            {t('leads.detail.consent.grantBody')}
          </Text>

          <VStack spacing="xs">
            <Text variant="caption" color={colors.text.tertiary}>
              {t('leads.detail.consent.sourceLabel')}
            </Text>
            <Select
              variant="dropdown"
              options={sourceOptions}
              value={source}
              onChange={(value) => setSource(value as ConsentSource)}
              testID="consent-source-select"
            />
          </VStack>

          <VStack spacing="xs">
            <Text variant="caption" color={colors.text.tertiary}>
              {t('leads.detail.consent.evidenceUrlLabel')}
            </Text>
            <FormInput
              value={evidenceUrl}
              onChangeText={setEvidenceUrl}
              placeholder={t('leads.detail.consent.evidenceUrlPlaceholder')}
              autoCapitalize="none"
              keyboardType="url"
              accessibilityLabel={t('leads.detail.consent.evidenceUrlLabel')}
              testID="consent-evidence-input"
            />
          </VStack>

          <VStack spacing="xs">
            <Text variant="caption" color={colors.text.tertiary}>
              {t('leads.detail.consent.dateObtainedLabel')}
            </Text>
            <FormInput
              value={occurredAt}
              onChangeText={setOccurredAt}
              placeholder={t('leads.detail.consent.dateObtainedPlaceholder')}
              autoCapitalize="none"
              accessibilityLabel={t('leads.detail.consent.dateObtainedLabel')}
              testID="consent-date-input"
            />
            {dateError ? (
              <Text variant="caption" color={colors.status.error.main}>
                {dateError}
              </Text>
            ) : null}
          </VStack>

          <Button
            variant="primary"
            onPress={submitGrant}
            accessibilityLabel={t('leads.detail.consent.submit')}
            accessibilityState={{ busy: recordConsent.isPending }}
            disabled={recordConsent.isPending}
            testID="consent-submit"
          >
            {recordConsent.isPending
              ? t('leads.detail.consent.saving')
              : t('leads.detail.consent.submit')}
          </Button>
        </VStack>
      </Sheet>
    </HStack>
  );
}
